/**
 * calendar adapter — calendar.peek, provider 'google', tier free.
 *
 * CLIENT-ONLY adapter (ADR-004): calendar data is personal, fetched
 * browser → Google Calendar API directly using the user's own OAuth token.
 * Nothing personal ever transits our Lambda or DynamoDB.
 *
 * CLIENT / SERVER SPLIT (ADR-002 §5):
 *   All members are CLIENT-SIDE. No buildRequest/responseSchema/normalize —
 *   this adapter uses ClientAdapter<Params, Data>, not ProviderAdapter.
 *
 * Glance grammar rulings (glance-grammar-spec §1.1, §1.3 Calendar row):
 *
 * primary (≤ 8 chars):
 *   nextEventTitle, clamped to 8 chars by GLANCE_LIMITS.
 *   No-events case: 'Clear' (calm, communicates calendar state without alarm).
 *   Ruling: 'Clear' is 5 chars — within limit; descriptive; calm brand.
 *
 * delta (≤ 20 chars):
 *   "in 42m" / "in 3h 10m" — humanized from minutesUntilNext.
 *   No-events case: null (no delta needed when calendar is clear).
 *   Ruling: 20 chars is enough for "in 99h 59m" (10 chars). Minutes-only
 *   up to 59m ("in 59m", 6 chars); hours+minutes above ("in 1h 5m", 8 chars).
 *   Max realistic: "in 48h 0m" for lookahead boundary edge cases (9 chars).
 *   Truncation rule from spec: slice at 20 chars, no ellipsis — these strings
 *   are short enough that truncation is impossible in practice.
 *
 * state (glanceState):
 *   - 'live' when minutesUntilNext ≤ 15 (imminent event — spec: live = blue accent)
 *   - 'neutral' otherwise (no alarm for calendar — calm brand)
 *   - allDayCount > 0 with no timed events: 'neutral' (all-day doesn't feel urgent)
 *   Ruling: we use 'live' only for genuinely imminent (≤15m) to match the
 *   spec semantics ("game in progress" → "event starting soon"). We do NOT
 *   use 'alert' because that implies a warning; an upcoming meeting is not
 *   an error. 'neutral' for all-day events because they have no urgency.
 *
 * Immutability: exported adapter object is frozen; glanceView returns new object.
 */
import { z } from 'zod';
import {
  calendarParamsSchema,
  deriveCacheKey,
  type CalendarParams,
} from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { ClientAdapter, GlanceViewModel } from 'briefpane/contracts';
import type { CalendarData } from './types.js';

/* -------------------------------------------------------------------------- */
/* Data shape schema — mirrors CalendarData fields                              */
/* -------------------------------------------------------------------------- */

export const calendarDataShape = z.object({
  nextEventTitle: z.string(),
  nextEventStartISO: z.string(),
  minutesUntilNext: z.number().int().min(0),
  eventsRemainingToday: z.number().int().min(0),
  allDayCount: z.number().int().min(0),
});

/* -------------------------------------------------------------------------- */
/* Delta humanisation                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Format minutesUntilNext into a humanized delta string (≤ 20 chars).
 *
 * Rulings:
 *   0–59 min  → "in 5m"     (max 6 chars at "in 59m")
 *   60+ min   → "in 1h 5m"  (max 10 chars at "in 99h 59m")
 *   0         → null (if there is no next event)
 *
 * Returns null when minutes is 0 (no event — primary will be 'Clear').
 */
export function humanizeDelta(minutes: number, hasEvent: boolean): string | null {
  if (!hasEvent) return null;
  if (minutes <= 0) return 'now';
  if (minutes < 60) return `in ${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `in ${h}h`;
  return `in ${h}h ${m}m`;
}

/* -------------------------------------------------------------------------- */
/* Glance view                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Map CalendarData + GlanceMap → GlanceViewModel.
 *
 * glance-grammar-spec §1.3 (Calendar peek row):
 *   primary : nextEventTitle (clamped to 8 chars), or 'Clear' if no events
 *   delta   : "in 42m" / "in 3h 10m" from minutesUntilNext, or null
 *   state   : 'live' when minutesUntilNext ≤ 15, else 'neutral'
 *
 * Pure. Returns a new object every call.
 */
export function calendarGlanceView(
  data: CalendarData,
  _glance: GlanceMap,
): GlanceViewModel {
  const hasEvent = Boolean(data.nextEventTitle);
  // D-018: text primaries carry a phrase (≤ 80-char safety bound); the
  // renderer's CSS ellipsis handles visual fit — "Very Lon" informed nobody.
  const primary = hasEvent ? data.nextEventTitle.slice(0, 80) : 'Clear';
  const delta = humanizeDelta(data.minutesUntilNext, hasEvent);

  // State ruling (documented above):
  //   live    → event within 15 minutes
  //   neutral → everything else (no-events, all-day only, > 15m away)
  const glanceState =
    hasEvent && data.minutesUntilNext <= 15 ? 'live' : 'neutral';

  return {
    dataState: 'ok',
    glanceState,
    slots: { primary, delta },
    label: null,
    fetchedAt: Date.now(),
  };
}

/* -------------------------------------------------------------------------- */
/* Adapter                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The production calendar adapter.
 *
 * Satisfies ClientAdapter<CalendarParams, CalendarData> — the subset of
 * ProviderAdapter shipped to the client bundle. Server-only members
 * (buildRequest, responseSchema, normalize) are absent because calendar.peek
 * is clientDirect and fetches browser → Google directly (ADR-004).
 */
export const calendarAdapter: ClientAdapter<CalendarParams, CalendarData> = Object.freeze({
  type: 'calendar.peek' as const,
  provider: 'google' as const,
  tier: 'free' as const,
  ttlSec: 300,
  paramsSchema: calendarParamsSchema,
  dataShape: calendarDataShape,

  cacheKey: (params: CalendarParams): string =>
    deriveCacheKey({ type: 'calendar.peek', provider: 'google', params }),

  glanceView: calendarGlanceView,
});
